/**
 * useOfflineData Hook - React hook for offline-first data access
 * 
 * Provides data from IndexedDB with automatic sync to server.
 */

'use client'

import { useState, useEffect, useCallback } from 'react'
import { offlineDB, isClient, isOnline, type OfflineBooking, type OfflinePassenger } from './db'
import { addToSyncQueue, processSyncQueue } from './sync'

// ============================================
// useOnlineStatus Hook
// ============================================

export function useOnlineStatus() {
    const [online, setOnline] = useState(true)

    useEffect(() => {
        if (!isClient()) return

        setOnline(navigator.onLine)

        const handleOnline = () => setOnline(true)
        const handleOffline = () => setOnline(false)

        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        return () => {
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('offline', handleOffline)
        }
    }, [])

    return online
}

// ============================================
// usePendingSyncCount Hook
// ============================================

export function usePendingSyncCount() {
    const [count, setCount] = useState(0)

    useEffect(() => {
        if (!isClient()) return

        const updateCount = async () => {
            const pending = await offlineDB.syncQueue.where('status').equals('pending').count()
            setCount(pending)
        }

        updateCount()

        // Poll every 5 seconds
        const interval = setInterval(updateCount, 5000)
        return () => clearInterval(interval)
    }, [])

    return count
}

// ============================================
// useOfflineBookings Hook
// ============================================

export function useOfflineBookings() {
    const [bookings, setBookings] = useState<OfflineBooking[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const online = useOnlineStatus()

    // Load bookings from IndexedDB or server
    const load = useCallback(async () => {
        if (!isClient()) return

        setLoading(true)
        setError(null)

        try {
            if (online) {
                // Online: fetch from server and cache
                const response = await fetch('/api/bookings')
                if (response.ok) {
                    const data = await response.json()
                    const bookingsData = data.bookings || data || []

                    // Cache to IndexedDB
                    await offlineDB.bookings.clear()
                    for (const booking of bookingsData) {
                        await offlineDB.bookings.put({
                            ...booking,
                            _syncStatus: 'synced',
                            _localUpdatedAt: new Date()
                        })
                    }

                    setBookings(bookingsData)
                } else {
                    throw new Error('Failed to fetch bookings')
                }
            } else {
                // Offline: load from IndexedDB
                const cached = await offlineDB.bookings.toArray()
                setBookings(cached)
            }
        } catch (err) {
            console.error('[Offline] Failed to load bookings:', err)
            // Fallback to cached data
            const cached = await offlineDB.bookings.toArray()
            setBookings(cached)
            if (cached.length === 0) {
                setError('Unable to load bookings. Please check your connection.')
            }
        } finally {
            setLoading(false)
        }
    }, [online])

    useEffect(() => {
        load()
    }, [load])

    return { bookings, loading, error, refresh: load }
}

// ============================================
// useOfflineBooking Hook (single booking)
// ============================================

export function useOfflineBooking(bookingId: string) {
    const [booking, setBooking] = useState<OfflineBooking | null>(null)
    const [loading, setLoading] = useState(true)
    const online = useOnlineStatus()

    const load = useCallback(async () => {
        if (!isClient() || !bookingId) return

        setLoading(true)

        try {
            if (online) {
                // Fetch from server
                const response = await fetch(`/api/bookings/${bookingId}`)
                if (response.ok) {
                    const data = await response.json()

                    // Cache to IndexedDB
                    await offlineDB.bookings.put({
                        ...data,
                        _syncStatus: 'synced',
                        _localUpdatedAt: new Date()
                    })

                    setBooking(data)
                }
            } else {
                // Load from cache
                const cached = await offlineDB.bookings.get(bookingId)
                setBooking(cached || null)
            }
        } catch (err) {
            // Fallback to cache
            const cached = await offlineDB.bookings.get(bookingId)
            setBooking(cached || null)
        } finally {
            setLoading(false)
        }
    }, [bookingId, online])

    // Update booking (works offline)
    const update = useCallback(async (updates: Partial<OfflineBooking>) => {
        if (!bookingId || !booking) return

        const now = new Date()
        const updatedBooking: OfflineBooking = {
            ...booking,
            ...updates,
            id: bookingId,
            _syncStatus: 'pending',
            _localUpdatedAt: now
        }

        // Save to IndexedDB immediately
        await offlineDB.bookings.put(updatedBooking)
        setBooking(updatedBooking)

        // Add to sync queue
        await addToSyncQueue('booking', bookingId, 'update', updates)

    }, [bookingId, booking])

    useEffect(() => {
        load()
    }, [load])

    return { booking, loading, update, refresh: load }
}

// ============================================
// useOfflinePassengers Hook
// ============================================

export function useOfflinePassengers(bookingId: string) {
    const [passengers, setPassengers] = useState<OfflinePassenger[]>([])
    const [loading, setLoading] = useState(true)
    const online = useOnlineStatus()

    const load = useCallback(async () => {
        if (!isClient() || !bookingId) return

        setLoading(true)

        try {
            if (online) {
                // Fetch from server
                const response = await fetch(`/api/bookings/${bookingId}/passengers`)
                if (response.ok) {
                    const data = await response.json()
                    const passengersList = data.passengers || data || []

                    // Cache to IndexedDB
                    // First clear passengers for this booking
                    await offlineDB.passengers.where('bookingId').equals(bookingId).delete()

                    for (const p of passengersList) {
                        await offlineDB.passengers.put({
                            ...p,
                            bookingId,
                            _syncStatus: 'synced',
                            _localUpdatedAt: new Date()
                        })
                    }

                    setPassengers(passengersList)
                }
            } else {
                // Load from cache
                const cached = await offlineDB.passengers.where('bookingId').equals(bookingId).toArray()
                setPassengers(cached)
            }
        } catch (err) {
            // Fallback to cache
            const cached = await offlineDB.passengers.where('bookingId').equals(bookingId).toArray()
            setPassengers(cached)
        } finally {
            setLoading(false)
        }
    }, [bookingId, online])

    // Add passenger (works offline)
    const addPassenger = useCallback(async (passenger: Omit<OfflinePassenger, 'id' | '_syncStatus' | '_localUpdatedAt'>) => {
        const id = crypto.randomUUID()
        const now = new Date()

        const newPassenger: OfflinePassenger = {
            ...passenger,
            id,
            bookingId,
            _syncStatus: 'pending',
            _localUpdatedAt: now
        }

        await offlineDB.passengers.put(newPassenger)
        setPassengers(prev => [...prev, newPassenger])

        await addToSyncQueue('passenger', id, 'create', { ...passenger, bookingId })

        return newPassenger
    }, [bookingId])

    // Update passenger (works offline)
    const updatePassenger = useCallback(async (passengerId: string, updates: Partial<OfflinePassenger>) => {
        const existing = await offlineDB.passengers.get(passengerId)
        if (!existing) return

        const updated: OfflinePassenger = {
            ...existing,
            ...updates,
            _syncStatus: 'pending',
            _localUpdatedAt: new Date()
        }

        await offlineDB.passengers.put(updated)
        setPassengers(prev => prev.map(p => p.id === passengerId ? updated : p))

        await addToSyncQueue('passenger', passengerId, 'update', { ...updates, bookingId })

    }, [bookingId])

    // Delete passenger (works offline)
    const deletePassenger = useCallback(async (passengerId: string) => {
        await offlineDB.passengers.delete(passengerId)
        setPassengers(prev => prev.filter(p => p.id !== passengerId))

        await addToSyncQueue('passenger', passengerId, 'delete', { bookingId })

    }, [bookingId])

    useEffect(() => {
        load()
    }, [load])

    return {
        passengers,
        loading,
        addPassenger,
        updatePassenger,
        deletePassenger,
        refresh: load
    }
}
